import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "@/components/theme-provider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ExternalLink, Hash, Layers, RefreshCw, Shield } from "lucide-react";
import BackButton from "components/navigation/back-button";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { ShowMore } from "components/shared/show-more";
import { StyledMarkdown } from "components/shared/styled-markdown/styled-markdown";
import { getCatalogueEntry, resolveCatalogueSlug } from "content/catalogue";
import * as QueryKeys from "data";
import { DatasetDefinitions } from "data/models";
import { fetchDefinitions, fetchUserLicences } from "data/queries-common";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { Redirect, useParams } from "react-router";
import * as Routes from "routes";
import { CatalogueActionsCard } from "./catalogue-actions-card";
import { CatalogueEntryCaseStudies } from "./catalogue-entry-case-studies";
import { CatalogueEntryDataDictionary } from "./catalogue-entry-data-dictionary";
import { CatalogueEntryCoverage } from "./catalogue-entry-coverage";
import { CatalogueEntryMetadataCard } from "./catalogue-entry-metadata-card";

const StatChip = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5">
    <Icon className="size-4 text-muted-foreground" />
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

/**
 * Planning Insights is keyed with a " - BASE" tier suffix by the datasets API
 * but without one by the licence list, so match on either form.
 */
const licenceCodesFor = (datasetProductCode: string): string[] => {
  const withoutTier = datasetProductCode.replace(/ - BASE$/, "");
  return withoutTier === datasetProductCode
    ? [datasetProductCode]
    : [datasetProductCode, withoutTier];
};

export const CatalogueEntry = () => {
  const { productName } = useParams<{ productName: string }>();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Legacy URLs used the product code, so they resolve here and redirect below.
  const canonicalSlug = resolveCatalogueSlug(productName);
  const entry = canonicalSlug ? getCatalogueEntry(canonicalSlug) : undefined;

  // The heavy fields live in a per-dataset chunk. It ships with the bundle, so
  // it can never go stale.
  const detail = useQuery({
    queryKey: [QueryKeys.catalogueDetailKey, canonicalSlug],
    queryFn: () => entry!.detail(),
    enabled: entry !== undefined,
    staleTime: Infinity,
  });

  const userLicences = useQuery({
    queryKey: [QueryKeys.userLicences],
    queryFn: () => fetchUserLicences(),
  });

  const datasetDefinitions: UseQueryResult<DatasetDefinitions> = useQuery({
    queryKey: [QueryKeys.definitionsKey],
    queryFn: () => fetchDefinitions(),
  });

  if (entry === undefined) {
    return (
      <div className="relative min-w-0 space-y-4">
        <div className="flex justify-end gap-4">
          <BackButton />
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Dataset not found</CardTitle>
            <CardDescription>
              No dataset matches “{productName}”. It may have been renamed or
              removed.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (canonicalSlug !== productName) {
    return <Redirect to={`${Routes.dataCatalogue}/${canonicalSlug}`} />;
  }

  const datasetId = entry.datasetProductCode;
  const licenceCodes = licenceCodesFor(datasetId);
  const licenced = Boolean(
    userLicences.data?.dataset_products?.some((product) =>
      licenceCodes.includes(product.product_name),
    ),
  );
  const definitionsMetadata = datasetDefinitions.data?.metadata[datasetId];

  return (
    <div className="relative min-w-0 space-y-4">
      <div className="flex justify-end gap-4">
        <BackButton />
      </div>

      <ReactQueryErrorWrapper
        queries={[detail, userLicences, datasetDefinitions]}
        mutations={[]}
      />

      <main className="grid gap-4 lg:grid-cols-3">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-4 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-semibold tracking-tight">
                {entry.displayName}
              </CardTitle>
              {entry.subtitle && (
                <CardDescription>{entry.subtitle}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                {entry.metadata.dataType.length > 0 && (
                  <StatChip
                    icon={Layers}
                    label="Type"
                    value={entry.metadata.dataType.join(", ")}
                  />
                )}
                <StatChip
                  icon={Hash}
                  label="Features"
                  value={entry.metadata.totalFeatureCount.toLocaleString()}
                />
                <StatChip
                  icon={RefreshCw}
                  label="Updated"
                  value={entry.metadata.updateFrequency}
                />
                <StatChip
                  icon={Shield}
                  label="Licence"
                  value={entry.metadata.license}
                />
              </div>

              <ShowMore>
                <div className="text-sm text-muted-foreground prose max-w-none [&_p:first-child]:mt-0 [&_p:last-child]:mb-[0.625em]">
                  {detail.data ? (
                    <StyledMarkdown>{detail.data.description}</StyledMarkdown>
                  ) : (
                    <StyledMarkdown>{entry.shortDescription}</StyledMarkdown>
                  )}
                </div>
              </ShowMore>

              {(entry.productDescriptionLink ||
                entry.latestReleaseReportLink ||
                entry.metadata.licenseLink) && (
                <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-4 text-sm">
                  {entry.productDescriptionLink && (
                    <a
                      href={entry.productDescriptionLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      Product Description
                      <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                  )}
                  {entry.latestReleaseReportLink && (
                    <a
                      href={entry.latestReleaseReportLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      Latest Release Report
                      <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                  )}
                  {entry.metadata.licenseLink && (
                    <a
                      href={entry.metadata.licenseLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      Licence Terms
                      <ExternalLink className="size-3.5 shrink-0" />
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {detail.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <CatalogueEntryCaseStudies
                caseStudies={detail.data?.caseStudies}
              />
              <CatalogueEntryDataDictionary
                tables={detail.data?.dataDictionaries}
              />
            </>
          )}
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-4 lg:max-w-100 ">
          {userLicences.isPending ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <CatalogueActionsCard
              licenced={licenced}
              datasetProductCode={datasetId}
              displayName={entry.displayName}
            />
          )}
          <CatalogueEntryCoverage
            datasetId={datasetId}
            image={isDark ? entry.imageDark : entry.imageLight}
          />
          {datasetDefinitions.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            definitionsMetadata && (
              <CatalogueEntryMetadataCard
                metadata={entry.metadata}
                definitionsMetadata={definitionsMetadata}
              />
            )
          )}
        </div>
      </main>
    </div>
  );
};
